import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Paper, Typography, Button, Breadcrumbs, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Menu, MenuItem } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "select2/dist/css/select2.min.css";
import "select2";
import "./UserManagement.css";
import { privilegeAPI, masterDataAPI, menuAPI, userAPI } from "../services/api";
import { getRoutePermissions } from "../utils/auth";
import Swal from "sweetalert2";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getDataTableExportRows } from "../utils/dataTableExportUtils";

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

const textOrEmpty = (value) => {
  const text = String(value ?? "").trim();
  if (!text || text === "-" || text.toLowerCase() === "null") return "";
  return text;
};

const isInternalUserId = (value) => /^U\d+$/i.test(textOrEmpty(value));

const firstDisplayName = (...values) => {
  for (const value of values) {
    const text = textOrEmpty(value);
    if (!text || isInternalUserId(text)) continue;
    return text;
  }
  return "";
};

const firstEmployeeCode = (...values) => {
  for (const value of values) {
    const text = textOrEmpty(value);
    if (!text || isInternalUserId(text)) continue;
    return text;
  }
  return "";
};

/** True when this mapping belongs to a user that still exists in UserMaster. */
const rowExistsInUserMaster = (row, usersById) => {
  const userId = textOrEmpty(row?.userIdCode || row?.UserIdCode);
  if (usersById && usersById.size > 0) {
    return Boolean(userId && usersById.has(userId));
  }
  return Boolean(
    firstEmployeeCode(row?.employeeCode, row?.EmployeeCode) ||
      firstDisplayName(
        row?.fullName,
        row?.FullName,
        row?.displayName,
        row?.DisplayName,
        row?.userName,
        row?.UserName
      )
  );
};

/** Emp ID and User Name come from UserMaster. Privilege text comes from UserPrivilegeMapping. */
const enrichUserMappingRow = (row, usersById) => {
  const userId = textOrEmpty(row?.userIdCode || row?.UserIdCode);
  const master = userId && usersById ? usersById.get(userId) : null;
  const fullName = firstDisplayName(
    row?.fullName,
    row?.FullName,
    row?.displayName,
    row?.DisplayName,
    master?.fullName,
    master?.FullName,
    master?.displayName,
    master?.DisplayName,
    row?.userName,
    row?.UserName,
    master?.userName,
    master?.UserName
  );
  return {
    ...row,
    userIdCode: userId || row?.userIdCode || row?.UserIdCode || "",
    employeeCode:
      firstEmployeeCode(
        row?.employeeCode,
        row?.EmployeeCode,
        master?.employeeCode,
        master?.EmployeeCode
      ) || "-",
    fullName,
    displayName: firstDisplayName(
      row?.displayName,
      row?.DisplayName,
      master?.displayName,
      master?.DisplayName,
      fullName
    ),
    userName: fullName,
    userTypeCode:
      row?.userTypeCode || row?.UserTypeCode || master?.userTypeCode || master?.UserTypeCode || "",
    userTypeName:
      row?.userTypeName || row?.UserTypeName || master?.userTypeName || master?.UserTypeName || "",
  };
};

const PrivilegeManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const routePermissions = getRoutePermissions(location.pathname);
  const { canView, canAdd, canEdit, canDelete } = routePermissions;
  const [assignmentMode, setAssignmentMode] = useState("userType"); // "userType" or "user"
  const [filters, setFilters] = useState({
    userTypeCode: "",
    userIdCode: "all",
    menuCode: "all",
  });
  const [privileges, setPrivileges] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [users, setUsers] = useState([]);
  const usersByIdRef = useRef(new Map());
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(false);
  const tableRef = React.useRef(null);
  const dataTableInstanceRef = React.useRef(null);
  const userTypeSelectRef = React.useRef(null);
  const userSelectRef = React.useRef(null);
  const menuSelectRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
  const listStateRef = React.useRef({ assignmentMode, filters });
  listStateRef.current = { assignmentMode, filters };

  const roleUserCounts = React.useMemo(() => {
    const counts = {};
    (users || []).forEach((user) => {
      const active =
        user.isActive === true ||
        user.isActive === 1 ||
        user.IsActive === true ||
        user.IsActive === 1 ||
        (user.isActive == null && user.IsActive == null);
      if (!active) return;
      const code = user.userTypeCode || user.UserTypeCode || "";
      if (!code) return;
      counts[code] = (counts[code] || 0) + 1;
    });
    return counts;
  }, [users]);

  const goToUserWiseForRole = (userTypeCode) => {
    if (!userTypeCode) return;
    const nextFilters = {
      userTypeCode: String(userTypeCode),
      userIdCode: "all",
      menuCode: "all",
    };
    setLoading(true);
    setPrivileges([]);
    setAssignmentMode("user");
    setFilters(nextFilters);
    fetchPrivileges({ assignmentMode: "user", filters: nextFilters });
  };

  const getInitiatorUserTypeCode = (types = userTypes) => {
    const match = (types || []).find((t) => {
      const name = String(t.userTypeName || t.UserTypeName || "").toLowerCase().trim();
      const code = String(t.userTypeCode || t.UserTypeCode || "").toUpperCase().trim();
      return name === "initiator" || name.includes("initiator") || code === "UT0000001";
    });
    return match?.userTypeCode || match?.UserTypeCode || "";
  };

  const getDefaultFiltersForMode = (mode, types = userTypes) => {
    if (mode === "user") {
      return {
        userTypeCode: getInitiatorUserTypeCode(types),
        userIdCode: "all",
        menuCode: "all",
      };
    }
    return {
      userTypeCode: "",
      userIdCode: "all",
      menuCode: "all",
    };
  };

  const enrichPrivilegeRow = (privilege) => {
    const userId = privilege?.userIdCode ?? privilege?.UserIdCode ?? "";
    const matchedUser = userId
      ? users.find((u) => (u.userIdCode || u.UserIdCode) === userId)
      : null;
    const roleCode =
      privilege?.userTypeCode ||
      privilege?.UserTypeCode ||
      matchedUser?.userTypeCode ||
      matchedUser?.UserTypeCode ||
      "";
    const roleName =
      privilege?.userTypeName ||
      privilege?.UserTypeName ||
      userTypes.find((t) => (t.userTypeCode || t.UserTypeCode) === roleCode)?.userTypeName ||
      "";

    return {
      ...privilege,
      userIdCode: userId || privilege?.userIdCode || null,
      fullName:
        privilege?.fullName ||
        privilege?.FullName ||
        matchedUser?.fullName ||
        matchedUser?.FullName ||
        null,
      userName:
        privilege?.userName ||
        privilege?.UserName ||
        matchedUser?.userName ||
        matchedUser?.UserName ||
        null,
      userTypeCode: roleCode || privilege?.userTypeCode || "",
      userTypeName: roleName || privilege?.userTypeName || null,
    };
  };

  const renderPermissionIcon = (allowed) =>
    allowed
      ? '<i class="bi bi-check-circle-fill text-success"></i>'
      : '<span class="text-muted">-</span>';

  const hasAssignablePage = (row) => {
    const pageName = row?.pageName ?? row?.PageName ?? "";
    return Boolean(String(pageName).trim());
  };

  const getListReturnState = () => ({
    restorePrivilegesList: true,
    assignmentMode: listStateRef.current.assignmentMode,
    filters: {
      userTypeCode: listStateRef.current.filters.userTypeCode || "",
      userIdCode: listStateRef.current.filters.userIdCode || "all",
      menuCode: listStateRef.current.filters.menuCode || "all",
    },
  });

  const currentUser = (() => {
    try {
      const u = localStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (e) {
      return null;
    }
  })();

  useEffect(() => {
    if (!canView) {
      Swal.fire({
        title: "Access Denied",
        text: "You do not have permission to view this page.",
        icon: "error",
        confirmButtonText: "OK",
      }).then(() => navigate("/dashboard"));
      return;
    }
    isMountedRef.current = true;
    fetchUserTypes();
    fetchMenus();
    fetchUsers();

    const restore = location.state?.restorePrivilegesList;
    if (restore) {
      const mode = location.state.assignmentMode || "userType";
      const nextFilters = location.state.filters || getDefaultFiltersForMode(mode);
      setAssignmentMode(mode);
      setFilters(nextFilters);
      fetchPrivileges({ assignmentMode: mode, filters: nextFilters });
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      fetchPrivileges();
    }
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  useEffect(() => {
    if (assignmentMode === "user") {
      fetchUsers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentMode]);

  useEffect(() => {
    const map = new Map();
    (users || []).forEach((user) => {
      const id = textOrEmpty(user.userIdCode || user.UserIdCode);
      if (id) map.set(id, user);
    });
    usersByIdRef.current = map;
    if (!map.size || assignmentMode !== "user") return;
    setPrivileges((prev) => {
      if (!prev.length) return prev;
      let changed = false;
      const next = prev
        .filter((row) => rowExistsInUserMaster(row, map))
        .map((row) => {
          const enriched = enrichUserMappingRow(row, map);
          if (
            enriched.employeeCode !== row.employeeCode ||
            enriched.fullName !== row.fullName ||
            enriched.userName !== row.userName
          ) {
            changed = true;
          }
          return enriched;
        });
      if (next.length !== prev.length) changed = true;
      return changed ? next : prev;
    });
  }, [users, assignmentMode]);

  // Filter users based on selected userTypeCode
  const filteredUsers = React.useMemo(() => {
    if (assignmentMode === "user" && filters.userTypeCode) {
      return users.filter(
        (user) => (user.userTypeCode || user.UserTypeCode) === filters.userTypeCode
      );
    }
    return users;
  }, [users, filters.userTypeCode, assignmentMode]);

  // After user types load, default User Wise role to Initiator if still empty
  useEffect(() => {
    if (assignmentMode !== "user" || !userTypes.length || filters.userTypeCode) return;
    const initiatorCode = getInitiatorUserTypeCode(userTypes);
    if (!initiatorCode) return;
    const nextFilters = {
      ...filters,
      userTypeCode: initiatorCode,
      userIdCode: filters.userIdCode || "all",
    };
    setFilters(nextFilters);
    fetchPrivileges({ assignmentMode: "user", filters: nextFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTypes, assignmentMode]);

  // Initialize Select2 dropdowns
  useEffect(() => {
    if (!isMountedRef.current) return;

    // Cleanup function
    const cleanupSelect2 = () => {
      [userTypeSelectRef, userSelectRef, menuSelectRef].forEach((ref) => {
        if (ref.current) {
          try {
            const $select = $(ref.current);
            if ($select.data("select2")) {
              $select.off("change select2:select");
              if (ref.current.parentNode || document.body.contains(ref.current)) {
                $select.select2("destroy");
              }
            }
          } catch (e) {
            console.warn("Error destroying Select2:", e);
          }
        }
      });
    };

    // Cleanup first
    cleanupSelect2();

    // Initialize User Role dropdown based on mode
    if (userTypeSelectRef.current && userTypes.length > 0) {
      const $select = $(userTypeSelectRef.current);
      $select
        .off("change select2:select")
        .select2({
          width: "100%",
          placeholder: assignmentMode === "user" ? "Select User Role" : "All User Roles",
        })
        .on("change select2:select", function (e) {
          if (isMountedRef.current) {
            const value = e.target?.value || $(this).val() || "";
            handleFilterChange("userTypeCode", value);
            // In user mode, clear user selection when role changes
            if (assignmentMode === "user") {
              handleFilterChange("userIdCode", "all");
            }
          }
        });
      
      if (filters.userTypeCode) {
        $select.val(filters.userTypeCode).trigger("change.select2");
      }
    }

    if (menuSelectRef.current && menus.length > 0) {
      const $select = $(menuSelectRef.current);
      $select
        .off("change select2:select")
        .select2({
          width: "100%",
          placeholder: "Select Menu",
        })
        .on("change select2:select", function (e) {
          if (isMountedRef.current) {
            const value = e.target?.value || $(this).val() || "";
            handleFilterChange("menuCode", value);
          }
        });
      
      if (filters.menuCode) {
        $select.val(filters.menuCode).trigger("change.select2");
      }
    }

    // Initialize Select2 for User dropdown (when in user mode)
    if (assignmentMode === "user" && userSelectRef.current) {
      const $select = $(userSelectRef.current);
      
      // Destroy existing Select2 instance
      if ($select.data("select2")) {
        $select.select2("destroy");
      }
      
      $select
        .off("change select2:select")
        .select2({
          width: "100%",
          placeholder: filters.userTypeCode ? "All" : "Select User Role First",
        })
        .on("change select2:select", function (e) {
          if (isMountedRef.current) {
            const value = e.target?.value || $(this).val() || "all";
            handleFilterChange("userIdCode", value || "all");
          }
        });
      
      const selectedUser =
        filters.userIdCode &&
        filters.userIdCode !== "all" &&
        filteredUsers.some((u) => (u.userIdCode || u.UserIdCode) === filters.userIdCode)
          ? filters.userIdCode
          : "all";
      $select.val(selectedUser).trigger("change.select2");
    }

    return () => {
      cleanupSelect2();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userTypes, filteredUsers, menus, filters.userTypeCode, filters.userIdCode, filters.menuCode, assignmentMode]);

  // Initialize DataTable
  useEffect(() => {
    if (!tableRef.current || !isMountedRef.current) return;

    // Use setTimeout to ensure DOM is ready and avoid React cleanup conflicts
    const timeoutId = setTimeout(() => {
      if (!isMountedRef.current || !tableRef.current) return;

      // Cleanup previous instance (must clear thead/tbody to avoid column-count mismatch)
      if (tableRef.current) {
        try {
          const $table = $(tableRef.current);
          const $wrapper = $table.closest(".dataTables_wrapper, .dt-container");
          const $bindTarget = $wrapper.length ? $wrapper : $table;
          $bindTarget.off("click.privilegeActions");
          $table.off("click", ".edit-privilege");
          $table.off("click", ".delete-privilege");
          $table.off("click", ".status-toggle");

          if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableRef.current)) {
            $table.DataTable().clear().destroy();
          }
          // Remove any leftover header/body from previous column layout
          $table.empty();
          dataTableInstanceRef.current = null;
        } catch (e) {
          console.warn("Error destroying DataTable:", e);
          try {
            $(tableRef.current).empty();
          } catch (_) {
            /* ignore */
          }
          dataTableInstanceRef.current = null;
        }
      }

      if (!isMountedRef.current || !tableRef.current) return;

      try {
        if (!$.fn.DataTable) {
          DataTable($);
        }

        dataTableInstanceRef.current = $(tableRef.current).DataTable({
        data: privileges,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        pageLength: 100,
        lengthMenu: [25, 50, 100, 250, 500],
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        columns:
          assignmentMode === "user"
            ? [
                {
                  title: "Emp ID",
                  data: "employeeCode",
                  width: "110px",
                  render: (d, type, row) =>
                    firstEmployeeCode(d, row?.employeeCode, row?.EmployeeCode) || "-",
                },
                {
                  title: "User Name",
                  data: "fullName",
                  width: "180px",
                  render: (d, type, row) =>
                    firstDisplayName(
                      row?.fullName,
                      row?.FullName,
                      d,
                      row?.displayName,
                      row?.DisplayName,
                      row?.userName,
                      row?.UserName
                    ) || "-",
                },
                {
                  title: "Role",
                  data: "userTypeName",
                  width: "140px",
                  render: (d, type, row) =>
                    d || row.userTypeName || row.UserTypeName || row.userTypeCode || row.UserTypeCode || "-",
                },
                {
                  title: "Assigned Privileges",
                  data: "assignedPrivileges",
                  width: "55%",
                  render: (d, type, row) => {
                    const text =
                      d ||
                      row.assignedPrivileges ||
                      row.AssignedPrivileges ||
                      "-";
                    const safe = String(text)
                      .replace(/&/g, "&amp;")
                      .replace(/</g, "&lt;")
                      .replace(/>/g, "&gt;")
                      .replace(/"/g, "&quot;");
                    return `<span title="${safe}" style="display:inline-block;max-width:100%;white-space:normal;word-break:break-word;">${safe}</span>`;
                  },
                },
                {
                  title: "Privilege Status",
                  data: null,
                  orderable: false,
                  width: "110px",
                  className: "text-center",
                  render: (d) => {
                    const isActive = d.isActive === true || d.isActive === 1 || String(d.privilegeStatus || "").toLowerCase() === "active";
                    const toggleClass = isActive ? "active" : "inactive";
                    const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
                    const userId = d.userIdCode || d.UserIdCode || "";
                    if (!canEdit) {
                      return `<div class="d-flex justify-content-center">
                        <span title="${isActive ? "Active" : "Inactive"}"
                          style="background: transparent; border: none; padding: 0; font-size: 24px; color: ${isActive ? "#4caf50" : "#9e9e9e"}; cursor: default;">
                          <i class="bi ${toggleIcon}"></i>
                        </span>
                      </div>`;
                    }
                    return `<div class="d-flex justify-content-center">
                      <button type="button" class="btn btn-sm mapping-status-toggle ${toggleClass}"
                        data-userid="${userId}"
                        data-active="${isActive}"
                        title="${isActive ? "Click to deactivate all privileges for this user" : "Click to activate privileges for this user"}"
                        style="background: transparent; border: none; padding: 0; font-size: 24px; color: ${isActive ? "#4caf50" : "#9e9e9e"}; cursor: pointer;">
                        <i class="bi ${toggleIcon}"></i>
                      </button>
                    </div>`;
                  },
                },
                {
                  title: "Action",
                  data: null,
                  orderable: false,
                  searchable: false,
                  width: "90px",
                  className: "text-center",
                  render: (d) => {
                    if (!canEdit) {
                      return `<span class="text-muted">-</span>`;
                    }
                    const userId = d.userIdCode || d.UserIdCode || "";
                    const roleCode = d.userTypeCode || d.UserTypeCode || "";
                    return `<div class="d-flex justify-content-center gap-2">
                      <button type="button" class="btn btn-sm edit-user-mapping"
                        data-userid="${userId}"
                        data-usertype="${roleCode}"
                        title="Edit Privilege"
                        style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                        <i class="bi bi-pencil-fill"></i>
                      </button>
                    </div>`;
                  },
                },
              ]
            : [
          {
            title: "SNo.",
            data: null,
            render: (d, type, row, meta) => (meta.row + 1).toString(),
            className: "text-center",
          },
          {
            title: "User Role",
            data: "userTypeName",
            render: (d, type, row) => d || row.userTypeCode || "-",
          },
          {
            title: "User Count",
            data: null,
            className: "text-center",
            orderable: true,
            render: (d, type, row) => {
              const roleCode = row.userTypeCode || row.UserTypeCode || "";
              const count = roleUserCounts[roleCode] ?? 0;
              if (type === "sort" || type === "type") return count;
              return `<button type="button"
                class="btn btn-link role-user-count p-0"
                data-usertype="${roleCode}"
                title="View users under this role"
                style="font-weight:600;color:#5b6bc0;text-decoration:underline;min-width:32px;">
                ${count}
              </button>`;
            },
          },
          {
            title: "User",
            data: null,
            visible: false,
            render: (d, type, row) => {
              if (row.userIdCode) {
                return row.fullName || row.userName || row.userIdCode || "-";
              }
              return "-";
            },
          },
          {
            title: "Menu",
            data: "adminMenuName",
            render: (d, type, row) =>
              d ||
              row.adminMenuName ||
              row.AdminMenuName ||
              row.menuName ||
              row.MenuName ||
              row.menuCode ||
              row.MenuCode ||
              "-",
          },
          {
            title: "View",
            data: "viewPermission",
            render: (d) => renderPermissionIcon(d),
            className: "text-center",
          },
          {
            title: "Add",
            data: "addPermission",
            render: (d) => renderPermissionIcon(d),
            className: "text-center",
          },
          {
            title: "Edit",
            data: "editPermission",
            render: (d) => renderPermissionIcon(d),
            className: "text-center",
          },
          {
            title: "Delete",
            data: "deletePermission",
            render: (d) => renderPermissionIcon(d),
            className: "text-center",
          },
          {
            title: "Download",
            data: "downloadPermission",
            render: (d) => renderPermissionIcon(d),
            className: "text-center",
          },
          {
            title: "Status",
            data: null,
            orderable: false,
            render: (d) => {
              const isActive = d.isActive === true || d.isActive === 1;
              const toggleClass = isActive ? "active" : "inactive";
              const toggleIcon = isActive ? "bi-toggle-on" : "bi-toggle-off";
              if (!canEdit) {
                return `<div class="d-flex justify-content-center">
                  <span
                    title="${isActive ? "Active" : "Inactive"}"
                    style="background: transparent; border: none; padding: 0; font-size: 24px; color: ${
                      isActive ? "#4caf50" : "#9e9e9e"
                    }; cursor: default;">
                    <i class="bi ${toggleIcon}"></i>
                  </span>
                </div>`;
              }
              return `<div class="d-flex justify-content-center">
                <button type="button" class="btn btn-sm status-toggle ${toggleClass}" 
                  data-code="${d.privCode || d.PrivCode || ""}" 
                  data-active="${isActive}"
                  title="${
                    isActive ? "Click to deactivate" : "Click to activate"
                  }"
                  style="background: transparent; border: none; padding: 0; font-size: 24px; color: ${
                    isActive ? "#4caf50" : "#9e9e9e"
                  }; cursor: pointer;">
                  <i class="bi ${toggleIcon}"></i>
                </button>
              </div>`;
            },
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            searchable: false,
            render: (d) => {
              const privCode = d.privCode || d.PrivCode || "";
              const editBtn = canEdit
                ? `<button type="button" class="btn btn-sm edit-privilege" data-code="${privCode}" title="Edit Privilege"
                    style="background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-pencil-fill"></i>
                  </button>`
                : "";
              const deleteBtn = canDelete
                ? `<button type="button" class="btn btn-sm delete-privilege" data-code="${privCode}" title="Delete Privilege"
                    style="background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%); border: none; color: white; padding: 8px 10px; border-radius: 6px;">
                    <i class="bi bi-trash-fill"></i>
                  </button>`
                : "";
              const content = `${editBtn}${deleteBtn}`;
              return `<div class="d-flex justify-content-center gap-2">${
                content || `<span class="text-muted">-</span>`
              }</div>`;
            },
          },
        ],
      });

      // Bind on wrapper so clicks work with DataTables scrollX clones
      const $bindTarget = (() => {
        const $table = $(tableRef.current);
        const $wrapper = $table.closest(".dataTables_wrapper, .dt-container");
        return $wrapper.length ? $wrapper : $table;
      })();

      $bindTarget.off("click.privilegeActions");

      if (assignmentMode !== "user") {
        $bindTarget.on("click.privilegeActions", ".role-user-count", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const userTypeCode = $(this).attr("data-usertype") || $(this).data("usertype");
          if (userTypeCode && String(userTypeCode) !== "undefined") {
            goToUserWiseForRole(String(userTypeCode));
          }
        });
      }

      if (assignmentMode !== "user" && canEdit) {
        $bindTarget.on("click.privilegeActions", ".edit-privilege", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const privCode = $(this).attr("data-code") || $(this).data("code");
          if (privCode && String(privCode) !== "undefined") {
            navigate(`/privileges/edit/${encodeURIComponent(String(privCode))}`, {
              state: getListReturnState(),
            });
          }
        });
      }

      if (assignmentMode !== "user" && canDelete) {
        $bindTarget.on("click.privilegeActions", ".delete-privilege", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const privCode = $(this).attr("data-code") || $(this).data("code");
          if (privCode && String(privCode) !== "undefined") {
            handleDelete(privCode);
          }
        });
      }

      if (assignmentMode !== "user" && canEdit) {
        $bindTarget.on("click.privilegeActions", ".status-toggle", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const privCode = $(this).attr("data-code") || $(this).data("code");
          const currentActive =
            $(this).data("active") === true ||
            $(this).data("active") === "true" ||
            $(this).data("active") === 1 ||
            $(this).attr("data-active") === "true";
          const newActive = !currentActive;
          if (privCode && String(privCode) !== "undefined") {
            handleToggleStatus(privCode, newActive);
          }
        });
      }

      if (assignmentMode === "user" && canEdit) {
        $bindTarget.on("click.privilegeActions", ".mapping-status-toggle", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const userIdCode = $(this).attr("data-userid") || $(this).data("userid");
          const currentActive =
            $(this).data("active") === true ||
            $(this).data("active") === "true" ||
            $(this).data("active") === 1 ||
            $(this).attr("data-active") === "true";
          const newActive = !currentActive;
          if (userIdCode && String(userIdCode) !== "undefined") {
            handleToggleMappingStatus(String(userIdCode), newActive);
          }
        });

        $bindTarget.on("click.privilegeActions", ".edit-user-mapping", function (e) {
          e.preventDefault();
          e.stopPropagation();
          if (!isMountedRef.current) return;
          const userIdCode = $(this).attr("data-userid") || $(this).data("userid");
          const userTypeCode = $(this).attr("data-usertype") || $(this).data("usertype") || "";
          if (userIdCode && String(userIdCode) !== "undefined") {
            const qs = new URLSearchParams({
              mode: "user",
              userIdCode: String(userIdCode),
            });
            if (userTypeCode) qs.set("userTypeCode", String(userTypeCode));
            navigate(`/privileges/add?${qs.toString()}`, {
              state: getListReturnState(),
            });
          }
        });
      }
      } catch (error) {
        console.error("Error initializing DataTable:", error);
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => {
      clearTimeout(timeoutId);
      if (tableRef.current) {
        try {
          const $table = $(tableRef.current);
          const $wrapper = $table.closest(".dataTables_wrapper, .dt-container");
          const $bindTarget = $wrapper.length ? $wrapper : $table;
          $bindTarget.off("click.privilegeActions");
          if ($.fn.DataTable && $.fn.DataTable.isDataTable(tableRef.current)) {
            $table.DataTable().clear().destroy();
          }
          $table.empty();
        } catch (e) {
          console.warn("Error in DataTable cleanup:", e);
        } finally {
          dataTableInstanceRef.current = null;
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privileges, assignmentMode, canEdit, canDelete, roleUserCounts]);

  const fetchUserTypes = async () => {
    try {
      const response = await masterDataAPI.getUserTypes();
      if (response.success && response.data) {
        setUserTypes(response.data);
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
    }
  };

  const fetchMenus = async () => {
    try {
      const response = await menuAPI.getAllMenusForPrivilegePages();
      if (response.success && response.data) {
        setMenus(response.data);
      }
    } catch (error) {
      console.error("Error fetching menus:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      // Fetch all active users - we'll filter by userTypeCode on the frontend
      const response = await userAPI.getAll({ status: "active" });
      if (response.success && response.data) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchPrivileges = async (overrides = {}) => {
    try {
      setLoading(true);

      const mode = overrides.assignmentMode ?? assignmentMode;
      const currentFilters = { ...filters, ...(overrides.filters || {}) };

      // Read live Select2 values so Search uses what is shown in the UI
      const liveUserTypeCode =
        userTypeSelectRef.current && $(userTypeSelectRef.current).data("select2")
          ? ($(userTypeSelectRef.current).val() || "")
          : currentFilters.userTypeCode;
      const liveUserIdCode =
        (overrides.assignmentMode ?? assignmentMode) === "user" &&
        userSelectRef.current &&
        $(userSelectRef.current).data("select2")
          ? ($(userSelectRef.current).val() || "all")
          : currentFilters.userIdCode || "all";
      const liveMenuCode =
        menuSelectRef.current && $(menuSelectRef.current).data("select2")
          ? ($(menuSelectRef.current).val() || "all")
          : currentFilters.menuCode;

      const effectiveFilters = {
        userTypeCode: overrides.filters ? currentFilters.userTypeCode : liveUserTypeCode,
        userIdCode: overrides.filters ? currentFilters.userIdCode : liveUserIdCode,
        menuCode: overrides.filters ? currentFilters.menuCode : liveMenuCode,
      };

      // User Wise: load denormalized mapping table (one row per user)
      if (mode === "user") {
        const selectedUserId =
          effectiveFilters.userIdCode && effectiveFilters.userIdCode !== "all"
            ? effectiveFilters.userIdCode
            : undefined;

        const mappingParams = {
          userTypeCode: effectiveFilters.userTypeCode || undefined,
          userIdCode: selectedUserId,
          // Only active privilege mappings (deactivated rows stay hidden)
          isActive: true,
          pageNumber: 1,
          pageSize: 5000,
        };

        const response = await privilegeAPI.getUserMappings(mappingParams);
        if (response.success && response.data) {
          const mappings =
            response.data.mappings ||
            response.data.Mappings ||
            response.data.data ||
            [];
          setPrivileges(
            (mappings || [])
              .filter((m) => {
                const active = m.isActive ?? m.IsActive;
                const status = String(m.privilegeStatus || m.PrivilegeStatus || "").toLowerCase();
                return (
                  (active === true || active === 1 || status === "active") &&
                  rowExistsInUserMaster(m, usersByIdRef.current)
                );
              })
              .map((m) =>
                enrichUserMappingRow(
                  {
                    ...m,
                    assignedPrivileges: m.assignedPrivileges || m.AssignedPrivileges || "-",
                    privilegeStatus: m.privilegeStatus || m.PrivilegeStatus || "Inactive",
                    isActive: m.isActive ?? m.IsActive ?? true,
                  },
                  usersByIdRef.current
                )
              )
          );
        } else {
          setPrivileges([]);
        }
        return;
      }

      let userTypeCodeParam = effectiveFilters.userTypeCode || undefined;
      let userIdCodeParam = undefined;

      const params = {
        viewMode: mode,
        userTypeCode: userTypeCodeParam,
        userIdCode: userIdCodeParam,
        menuCode:
          effectiveFilters.menuCode === "all"
            ? undefined
            : effectiveFilters.menuCode || undefined,
        isActive: true,
        pageNumber: 1,
        pageSize: 5000,
      };

      const response = await privilegeAPI.getAll(params);
      if (response.success && response.data) {
        let privilegesList =
          response.data.privileges || response.data.Privileges || response.data.data || [];

        privilegesList = privilegesList
          .map(enrichPrivilegeRow)
          .filter((p) => {
            if (!hasAssignablePage(p)) return false;
            const isActive = p?.isActive === true || p?.isActive === 1 || p?.IsActive === true || p?.IsActive === 1;
            if (!isActive) return false;
            const userId = p?.userIdCode ?? p?.UserIdCode;
            const hasUser = Boolean(userId && String(userId).trim());
            return !hasUser;
          });

        setPrivileges(privilegesList);
      } else {
        setPrivileges([]);
      }
    } catch (error) {
      console.error("Error fetching privileges:", error);
      Swal.fire("Error", "Failed to fetch privileges", "error");
      setPrivileges([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => {
      const newFilters = { ...prev, [key]: value };
      // Clear user selection when userTypeCode changes in user mode
      if (key === "userTypeCode" && assignmentMode === "user") {
        newFilters.userIdCode = "all";
      }
      return newFilters;
    });
  };

  const handleSearch = () => {
    fetchPrivileges();
  };

  const handleReset = () => {
    const resetFilters = getDefaultFiltersForMode(assignmentMode);
    setFilters(resetFilters);
    fetchPrivileges({ filters: resetFilters });
  };

  const handleDelete = async (privCode) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const modifiedBy = currentUser?.userIdCode || currentUser?.UserIdCode || "SYSADMIN001";
        const response = await privilegeAPI.delete(privCode, modifiedBy);
        if (response.success) {
          Swal.fire("Deleted!", "Privilege has been deleted.", "success");
          fetchPrivileges();
        } else {
          Swal.fire("Error", response.message || "Failed to delete privilege", "error");
        }
      } catch (error) {
        console.error("Error deleting privilege:", error);
        Swal.fire("Error", "Failed to delete privilege", "error");
      }
    }
  };

  const handleToggleStatus = async (privCode, newStatus) => {
    try {
      const privilege = privileges.find((p) => p.privCode === privCode);
      if (!privilege) return;

      const modifiedBy = currentUser?.userIdCode || currentUser?.UserIdCode || "SYSADMIN001";
      const updateData = {
        privCode: privCode,
        addPermission: privilege.addPermission,
        editPermission: privilege.editPermission,
        viewPermission: privilege.viewPermission,
        deletePermission: privilege.deletePermission,
        downloadPermission: privilege.downloadPermission,
        isActive: newStatus,
        modifiedBy: modifiedBy,
      };

      const response = await privilegeAPI.update(privCode, updateData);
      if (response.success) {
        Swal.fire("Success", "Privilege status updated", "success");
        fetchPrivileges();
      } else {
        Swal.fire("Error", response.message || "Failed to update status", "error");
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      Swal.fire("Error", "Failed to update status", "error");
    }
  };

  const handleToggleMappingStatus = async (userIdCode, newStatus) => {
    try {
      const confirm = await Swal.fire({
        title: newStatus ? "Activate privileges?" : "Deactivate privileges?",
        text: newStatus
          ? "This will restore role-based privileges for this user."
          : "This will inactive all privileges for this user (menus and user-wise overrides).",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: newStatus ? "Yes, activate" : "Yes, deactivate",
        cancelButtonText: "Cancel",
      });
      if (!confirm.isConfirmed) return;

      const modifiedBy = currentUser?.userIdCode || currentUser?.UserIdCode || "SYSADMIN001";
      const response = await privilegeAPI.updateUserMappingStatus(userIdCode, newStatus, modifiedBy);
      if (response.success) {
        Swal.fire(
          "Success",
          response.message || `Privileges ${newStatus ? "activated" : "deactivated"} successfully`,
          "success"
        );
        fetchPrivileges();
      } else {
        Swal.fire("Error", response.message || "Failed to update privilege status", "error");
      }
    } catch (error) {
      console.error("Error toggling mapping status:", error);
      Swal.fire("Error", "Failed to update privilege status", "error");
    }
  };

  // Export to PDF
  const exportToPDF = () => {
    const exportRows = getDataTableExportRows(dataTableInstanceRef, privileges);
    try {
      if (!exportRows || exportRows.length === 0) {
        Swal.fire("Info", "No data to export", "info");
        return;
      }

      const doc = new jsPDF();
      doc.setFontSize(16);
      doc.text(
        assignmentMode === "user" ? "User-wise Privilege Mapping" : "Privilege Management Data",
        14,
        15
      );

      if (assignmentMode === "user") {
        const tableData = exportRows.map((row) => [
          String(row.employeeCode || row.EmployeeCode || "-"),
          String(row.fullName || row.userName || row.UserName || "-"),
          String(row.userTypeName || row.userTypeCode || "-"),
          String(row.assignedPrivileges || row.AssignedPrivileges || "-"),
          (row.isActive === true || row.isActive === 1 || String(row.privilegeStatus || "").toLowerCase() === "active")
            ? "Active"
            : "Inactive",
        ]);
        autoTable(doc, {
          head: [["Emp ID", "User Name", "Role", "Assigned Privileges", "Privilege Status"]],
          body: tableData,
          startY: 25,
          styles: { fontSize: 7 },
          headStyles: { fillColor: [102, 126, 234] },
          margin: { top: 25 },
        });
      } else {
        const tableData = exportRows.map((privilege, index) => [
          String(index + 1),
          String(privilege.userTypeName || privilege.userTypeCode || "-"),
          String(roleUserCounts[privilege.userTypeCode || privilege.UserTypeCode] ?? 0),
          String(privilege.userIdCode ? (privilege.fullName || privilege.userName || privilege.userIdCode) : "-"),
          String(
            privilege.adminMenuName ||
              privilege.AdminMenuName ||
              privilege.menuName ||
              privilege.MenuName ||
              privilege.menuCode ||
              privilege.MenuCode ||
              "-"
          ),
          privilege.viewPermission ? "Yes" : "No",
          privilege.addPermission ? "Yes" : "No",
          privilege.editPermission ? "Yes" : "No",
          privilege.deletePermission ? "Yes" : "No",
          privilege.downloadPermission ? "Yes" : "No",
          privilege.isActive ? "Active" : "Inactive",
        ]);
        autoTable(doc, {
          head: [["SNo.", "User Role", "User Count", "User", "Menu", "View", "Add", "Edit", "Delete", "Download", "Status"]],
          body: tableData,
          startY: 25,
          styles: { fontSize: 7 },
          headStyles: { fillColor: [102, 126, 234] },
          margin: { top: 25 },
        });
      }

      doc.save(`Privilege_Management_${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("Error exporting to PDF:", error);
      Swal.fire({
        title: "Error",
        text: `Failed to export PDF: ${error.message || "Unknown error"}. Please check the browser console for details.`,
        icon: "error",
      });
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const exportRows = getDataTableExportRows(dataTableInstanceRef, privileges);
    try {
      const headers =
        assignmentMode === "user"
          ? ["Emp ID", "User Name", "Role", "Assigned Privileges", "Privilege Status"]
          : ["SNo.", "User Role", "User Count", "User", "Menu", "View", "Add", "Edit", "Delete", "Download", "Status"];
      const csvData =
        assignmentMode === "user"
          ? exportRows.map((row) => [
              row.employeeCode || row.EmployeeCode || "",
              row.fullName || row.userName || row.UserName || "",
              row.userTypeName || row.userTypeCode || "",
              row.assignedPrivileges || row.AssignedPrivileges || "",
              row.isActive === true || row.isActive === 1 || String(row.privilegeStatus || "").toLowerCase() === "active"
                ? "Active"
                : "Inactive",
            ])
          : exportRows.map((privilege, index) => [
              index + 1,
              privilege.userTypeName || privilege.userTypeCode || "",
              roleUserCounts[privilege.userTypeCode || privilege.UserTypeCode] ?? 0,
              privilege.userIdCode ? (privilege.fullName || privilege.userName || privilege.userIdCode) : "",
              privilege.adminMenuName ||
                privilege.AdminMenuName ||
                privilege.menuName ||
                privilege.MenuName ||
                privilege.menuCode ||
                privilege.MenuCode ||
                "",
              privilege.viewPermission ? "Yes" : "No",
              privilege.addPermission ? "Yes" : "No",
              privilege.editPermission ? "Yes" : "No",
              privilege.deletePermission ? "Yes" : "No",
              privilege.downloadPermission ? "Yes" : "No",
              privilege.isActive ? "Active" : "Inactive",
            ]);

      const csvContent = [
        headers.join(","),
        ...csvData.map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Privilege_Management_${new Date().toISOString().split("T")[0]}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      Swal.fire("Error", "Failed to export CSV", "error");
    }
  };

  // Export to Excel
  const exportToExcel = async () => {
    // Export what the grid is actually showing: when a search is active
    // only the matching rows are written out, otherwise the full data set.
    const exportRows = getDataTableExportRows(dataTableInstanceRef, privileges);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Privilege Management");

      // Add title
      worksheet.mergeCells(assignmentMode === "user" ? "A1:E1" : "A1:K1");
      const titleRow = worksheet.getRow(1);
      titleRow.getCell(1).value =
        assignmentMode === "user" ? "User-wise Privilege Mapping" : "Privilege Management Data";
      titleRow.getCell(1).font = { size: 16, bold: true };
      titleRow.getCell(1).alignment = { horizontal: "center", vertical: "middle" };
      titleRow.height = 25;

      // Add headers
      const headerRow = worksheet.getRow(3);
      headerRow.values =
        assignmentMode === "user"
          ? ["Emp ID", "User Name", "Role", "Assigned Privileges", "Privilege Status"]
          : ["SNo.", "User Role", "User Count", "User", "Menu", "View", "Add", "Edit", "Delete", "Download", "Status"];
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FF667EEA" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };
      headerRow.height = 20;

      // Add data
      if (assignmentMode === "user") {
        exportRows.forEach((row) => {
          const excelRow = worksheet.addRow([
            row.employeeCode || row.EmployeeCode || "",
            row.fullName || row.userName || row.UserName || "",
            row.userTypeName || row.userTypeCode || "",
            row.assignedPrivileges || row.AssignedPrivileges || "",
            row.isActive === true || row.isActive === 1 || String(row.privilegeStatus || "").toLowerCase() === "active"
              ? "Active"
              : "Inactive",
          ]);
          excelRow.alignment = { vertical: "middle" };
        });
      } else {
      exportRows.forEach((privilege, index) => {
        const row = worksheet.addRow([
          index + 1,
          privilege.userTypeName || privilege.userTypeCode || "",
          roleUserCounts[privilege.userTypeCode || privilege.UserTypeCode] ?? 0,
          privilege.userIdCode ? (privilege.fullName || privilege.userName || privilege.userIdCode) : "",
          privilege.adminMenuName ||
          privilege.AdminMenuName ||
          privilege.menuName ||
          privilege.MenuName ||
          privilege.menuCode ||
          privilege.MenuCode ||
          "",
          privilege.viewPermission ? "Yes" : "No",
          privilege.addPermission ? "Yes" : "No",
          privilege.editPermission ? "Yes" : "No",
          privilege.deletePermission ? "Yes" : "No",
          privilege.downloadPermission ? "Yes" : "No",
          privilege.isActive ? "Active" : "Inactive",
        ]);
        row.alignment = { vertical: "middle" };
      });
      }

      // Set column widths
      worksheet.columns.forEach((column) => {
        column.width = 18;
      });

      // Generate buffer and download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `Privilege_Management_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      Swal.fire("Error", "Failed to export Excel", "error");
    }
  };

  // Handle export menu
  const handleExportMenuOpen = (event) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportOption = (format) => {
    handleExportMenuClose();
    if (format === "excel") {
      exportToExcel();
    } else if (format === "csv") {
      exportToCSV();
    } else if (format === "pdf") {
      exportToPDF();
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Breadcrumbs
            aria-label="breadcrumb"
            className="add-edit-user-breadcrumb"
          >
            <Typography
              color="text.primary"
              className="breadcrumb-current brdcrmbLstitms brdcrmbCurPg"
            >
              Privilege Management
            </Typography>
          </Breadcrumbs>
          <Typography variant="body2" sx={{ color: "#666666", mt: 1 }}>
            Manage User Roles & User wise privileges and permissions.
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          {/* Export Button with Dropdown */}
          <Button
            onClick={handleExportMenuOpen}
            sx={{
              background: "transparent",
              border: "1px solid #667eea",
              color: "#667eea",
              fontWeight: 500,
              padding: "8px 10px",
              borderRadius: "6px",
              transition: "all 0.3s ease",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "36px",
              height: "36px",
              "&:hover": {
                background: "transparent",
                borderColor: "#764ba2",
                color: "#764ba2",
              },
              "& .MuiSvgIcon-root": {
                color: "#667eea",
              },
              "&:hover .MuiSvgIcon-root": {
                color: "#764ba2",
              },
            }}
            title="Export"
          >
            <FileDownloadIcon sx={{ fontSize: "26px", filter: "drop-shadow(0 0 0.5px currentColor)" }} />
            <ArrowDropDownIcon sx={{ fontSize: "16px", ml: 0.5 }} />
          </Button>
          <Menu
            anchorEl={exportMenuAnchor}
            open={Boolean(exportMenuAnchor)}
            onClose={handleExportMenuClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={() => handleExportOption("excel")}>
              Excel
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("csv")}>
              CSV
            </MenuItem>
            <MenuItem onClick={() => handleExportOption("pdf")}>
              PDF
            </MenuItem>
          </Menu>
          {canAdd && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                navigate("/privileges/add", {
                  state: getListReturnState(),
                })
              }
              sx={{
                background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                border: "none",
                color: "white",
                fontWeight: 600,
                fontSize: "11px",
                boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
                borderRadius: "4px",
                textTransform: "uppercase",
                padding: "8px 24px",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
                  boxShadow: "0 6px 20px rgba(245, 87, 108, 0.6)",
                  transform: "translateY(-2px)",
                },
                transition: "all 0.3s ease",
              }}
            >
              Add Privilege
            </Button>
          )}
        </Box>
      </Box>

      {/* Filter Panel */}
      <div className="card-panel mb-3 filter-panel">
        <div
          className="section-heading d-flex align-items-center gap-2 mb-3"
          style={{ marginBottom: "14px" }}
        >
          <SearchIcon
            fontSize="small"
            className="text-muted"
            style={{
              width: "18px",
              height: "18px",
              flexShrink: 0,
              marginTop: "2px",
            }}
          />
          <span>Filter: {assignmentMode === "userType" ? "User Role and Menu" : "User Role and User"}</span>
        </div>

        <div className="container-fluid">
          {/* Assignment Mode Selection */}
          <div className="row mb-3">
            <div className="col-12">
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ mb: 1, fontWeight: "bold", fontSize: "14px" }}>
                  View Mode *
                </FormLabel>
                <RadioGroup
                  row
                  value={assignmentMode}
                  onChange={(e) => {
                    const newMode = e.target.value;
                    const resetFilters = getDefaultFiltersForMode(newMode);
                    setLoading(true);
                    setPrivileges([]);
                    setAssignmentMode(newMode);
                    setFilters(resetFilters);
                    fetchPrivileges({ assignmentMode: newMode, filters: resetFilters });
                  }}
                >
                  <FormControlLabel
                    value="userType"
                    control={<Radio size="small" />}
                    label="User Role Wise"
                  />
                  <FormControlLabel
                    value="user"
                    control={<Radio size="small" />}
                    label="User Wise"
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>

          <div className="row g-3 align-items-end">
            {assignmentMode === "userType" && (
              <div className="col-12 col-md-6 col-lg-3">
                <label className="input-label d-block mb-1 text-start">
                  User Role
                </label>
                <select
                  ref={userTypeSelectRef}
                  className="form-select form-control-light filter-select text-start"
                  value={filters.userTypeCode}
                  style={{ height: "42px", textAlign: "left" }}
                >
                  <option value="">All User Roles</option>
                  {userTypes.map((type) => (
                    <option key={type.userTypeCode} value={type.userTypeCode}>
                      {type.userTypeName}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {assignmentMode === "user" && (
              <>
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="input-label d-block mb-1 text-start">
                    User Role *
                  </label>
                  <select
                    ref={userTypeSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    value={filters.userTypeCode}
                    style={{ height: "42px", textAlign: "left" }}
                  >
                    <option value="">Select User Role</option>
                    {userTypes.map((type) => (
                      <option key={type.userTypeCode} value={type.userTypeCode}>
                        {type.userTypeName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-md-6 col-lg-3">
                  <label className="input-label d-block mb-1 text-start">
                    User
                  </label>
                  <select
                    ref={userSelectRef}
                    className="form-select form-control-light filter-select text-start"
                    value={filters.userIdCode || "all"}
                    disabled={!filters.userTypeCode}
                    style={{ height: "42px", textAlign: "left" }}
                  >
                    <option value="all">
                      {filters.userTypeCode ? "All" : "Select User Role First"}
                    </option>
                    {filteredUsers.map((user) => (
                      <option key={user.userIdCode} value={user.userIdCode}>
                        {user.fullName || user.userName || user.userIdCode}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {assignmentMode === "userType" && (
            <div className="col-12 col-md-6 col-lg-3">
              <label className="input-label d-block mb-1 text-start">
                Menu
              </label>
              <select
                ref={menuSelectRef}
                className="form-select form-control-light filter-select text-start"
                value={filters.menuCode}
                style={{ height: "42px", textAlign: "left" }}
              >
                <option value="all">All</option>
                {menus
                  .filter((menu) => {
                    const pageName = menu.pageName || menu.PageName || "";
                    return Boolean(String(pageName).trim());
                  })
                  .map((menu) => (
                  <option key={menu.menuCode} value={menu.menuCode}>
                    {menu.adminMenuName ||
                      menu.AdminMenuName ||
                      menu.menuName ||
                      menu.MenuName ||
                      menu.menuCode}
                  </option>
                ))}
              </select>
            </div>
            )}

            <div className="col-12 col-md-6 col-lg-3">
              <label
                className="input-label d-block mb-1 text-start"
                style={{ visibility: "hidden" }}
              >
                Actions
              </label>
              <div className="d-flex gap-2 filter-buttons justify-content-start">
                <button
                  type="button"
                  className="btn btn-filter"
                  onClick={handleSearch}
                  style={{
                    background:
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #0f00f5 0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(102, 126, 234, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background =
                      "linear-gradient(135deg, #7a00f5  0%, #0f00f5 100%)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(102, 126, 234, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <SearchIcon fontSize="small" className="me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-reset-filter"
                  onClick={handleReset}
                  title="Reset"
                  style={{
                    background:
                      "linear-gradient(135deg, #84fab0 0%, #8fd3f4 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    boxShadow: "0 4px 15px rgba(132, 250, 176, 0.4)",
                    borderRadius: "4px",
                    padding: "8px 16px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow =
                      "0 6px 20px rgba(132, 250, 176, 0.6)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(132, 250, 176, 0.4)";
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                >
                  <RestartAltIcon fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DataTable */}
      <Paper
        sx={{
          p: 0,
          backgroundColor: "transparent",
          boxShadow: "none",
          border: "none",
        }}
      >
        <div className="card-panel mt-0 privilege-grid-shell">
          {loading ? (
            <Box
              sx={{
                minHeight: 420,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography>Loading privileges...</Typography>
            </Box>
          ) : privileges.length === 0 ? (
            <Box
              sx={{
                minHeight: 420,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No privileges found. Click "Add Privilege" to create a new privilege.
              </Typography>
            </Box>
          ) : (
            <div className="table-responsive privilege-grid-table-wrap">
              <table
                key={`privilege-grid-${assignmentMode}`}
                ref={tableRef}
                className="table table-striped table-bordered complaint-table custmTblLst"
                style={{ width: "100%" }}
              />
            </div>
          )}
        </div>
      </Paper>
    </Box>
  );
};

export default PrivilegeManagement;
